import { format, isValid, parseISO } from 'date-fns';
import { PackageResult, PromoCodeBySubject } from './types';

export type { PromoCodeBySubject };

/** The account's renewal promo codes, one entry per package. */
export type RenewalPromoCode = {
  schoolAccountPackagesId: number;
  packagesId: number;
  subjectsId: number;
  promoCode: string;
  /** Null or absent when the code never expires. */
  dateEnd?: string | null;
};

/** Which codes a renewal package change dropped, per package. */
export type RenewalPromoCodeWipe = {
  schoolAccountPackagesId: number;
  packagesId: number;
  subjectsId: number;
  bRenewalPromoCodeWiped: boolean;
};

/** One line of the applied-codes list. */
export type PromoCodeRow = {
  promoCode: string;
  /** What the code saved in this panel. */
  discount: number;
  applied: boolean;
  /** False until pricing has reported, when a row is neither applied nor beaten. */
  priced: boolean;
  /** dd/MM/yyyy, or null when the code has no expiry. */
  expiryDate: string | null;
};

const isRenewalPromoCode = (entry: any): boolean =>
  !!entry && typeof entry.promoCode === 'string' && entry.promoCode.length > 0;

/** Reads the code list off a get or set response; both return the complete resulting state. */
export const readRenewalPromoCodes = (responseData: any): RenewalPromoCode[] => {
  const entries = responseData && responseData.details && responseData.details.renewalPromoCodes;

  return Array.isArray(entries) ? entries.filter(isRenewalPromoCode) : [];
};

/** Reads the wipe flags off a setrenewalpackages response. */
export const readPromoCodeWipes = (responseData: any): RenewalPromoCodeWipe[] => {
  const entries = responseData && responseData.details && responseData.details.renewalPromoCodeWipes;

  return Array.isArray(entries) ? entries : [];
};

/** Keyed by subject, because the selected package changes as the school uses the grid. */
export const promoCodeBySubject = (codes: RenewalPromoCode[]): PromoCodeBySubject => {
  const bySubject: PromoCodeBySubject = {};

  codes.forEach((c) => {
    // One code per package, so a subject holding two keeps the first; the extra keeps its own row.
    if (bySubject[c.subjectsId] === undefined) {
      bySubject[c.subjectsId] = c.promoCode;
    }
  });

  return bySubject;
};

/** A panel's own codes. One spanning both panels appears in both. */
export const codesForSubjects = (codes: RenewalPromoCode[], subjectIds: number[]): RenewalPromoCode[] =>
  codes.filter((c) => subjectIds.indexOf(c.subjectsId) !== -1);

/** Wipes are per package, so a code covering two subjects survives on the one not wiped. */
export const applyPromoCodeWipes = (
  codes: RenewalPromoCode[],
  wipes: RenewalPromoCodeWipe[] | null | undefined
): RenewalPromoCode[] => {
  const wiped = (wipes || []).filter((w) => w.bRenewalPromoCodeWiped);

  if (wiped.length === 0) {
    return codes;
  }

  return codes.filter(
    (c) => !wiped.some((w) => w.schoolAccountPackagesId === c.schoolAccountPackagesId)
  );
};

/** A code held against a basket. Only renewal codes carry an expiry; quote slots hold the name alone. */
export type HeldPromoCode = { promoCode: string; dateEnd?: string | null };

/** Codes are matched without case, since SQL matches them that way. */
const sameCode = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const distinctCodes = (codes: HeldPromoCode[]): string[] =>
  codes.reduce((names: string[], c) => (names.indexOf(c.promoCode) === -1 ? [...names, c.promoCode] : names), []);

const displayExpiry = (value: string | null | undefined): string | null => {
  if (!value) return null;

  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : null;
};

/** Expiry belongs to the code, not the package, so any entry carrying one speaks for them all. */
const expiryFor = (codes: HeldPromoCode[], promoCode: string): string | null => {
  const dated = codes.find((c) => c.promoCode === promoCode && !!c.dateEnd);

  return displayExpiry(dated && dated.dateEnd);
};

/** A rejected code write and the renewal panel it came from. */
export type PanelRejection = { groupKey: string; message: string };

/** Each panel is its own basket, so a rejection shown in another would describe the wrong renewal. */
export const rejectionForPanel = (rejection: PanelRejection | null, groupKey: string): string =>
  rejection && rejection.groupKey === groupKey ? rejection.message : '';

/** The subjects a change wiped a code from, for the notice. */
export const wipedSubjectIds = (wipes: RenewalPromoCodeWipe[] | null | undefined): number[] =>
  (wipes || []).filter((w) => w.bRenewalPromoCodeWiped).map((w) => w.subjectsId);

/** Codes gone after a write, so a displaced one can be explained rather than just vanish. */
export const droppedCodes = (before: RenewalPromoCode[], after: RenewalPromoCode[]): string[] => {
  const remaining = distinctCodes(after);

  return distinctCodes(before).filter((code) => remaining.indexOf(code) === -1);
};

/** One row per distinct code. A code that won nothing keeps its row, so it stays removable. */
export const displayRows = (codes: HeldPromoCode[], packages: PackageResult[]): PromoCodeRow[] =>
  distinctCodes(codes)
    .sort()
    .map((promoCode) => {
      // Without case: a slot holds what the user typed, pricing answers in the server's casing.
      const won = packages.filter(
        (p) => !!p.promoCode && sameCode(p.promoCode, promoCode) && !!p.promoCodeDiscountApplied
      );

      return {
        promoCode,
        discount: won.reduce((total, p) => total + (p.netSaving || 0), 0),
        applied: won.length > 0,
        priced: packages.length > 0,
        expiryDate: expiryFor(codes, promoCode),
      };
    });
